import { useEffect, useState, type ReactNode } from "react";
import Card from "components/card";
import ToggleSwitch from "components/ui/ToggleSwitch";
import { useAppLanguage } from "hooks/useAppLanguage";
import {
  forceUpdateSettingsService,
  type ForceUpdateSettingsPayload,
} from "services/forceUpdateSettingsService";

const DEFAULT_FORM: ForceUpdateSettingsPayload = {
  force_update_enabled: false,
  soft_update_enabled: true,
  ios_min_version: "1.0.0",
  android_min_version: "1.0.0",
  ios_latest_version: "1.0.0",
  android_latest_version: "1.0.0",
  ios_min_build: 0,
  android_min_build: 0,
  ios_store_url: "",
  android_store_url: "",
  force_message_ar: "يجب تحديث التطبيق لمتابعة الاستخدام.",
  force_message_en: "Please update the app to continue using it.",
  soft_message_ar: "يتوفر إصدار جديد من التطبيق. حدّث الآن لتجربة أفضل.",
  soft_message_en: "A new version is available. Update now for the best experience.",
};

const VERSION_RE = /^\d+(\.\d+){0,3}$/;

const INPUT_CLASS =
  "mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white";

function SettingsField({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-navy-700 dark:text-white">{label}</span>
      {children}
      {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
      {error ? <p className="mt-1 text-xs text-red-500">{error}</p> : null}
    </label>
  );
}

const ForceUpdateSettingsPage = () => {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [form, setForm] = useState<ForceUpdateSettingsPayload>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const labels = {
    title: isArabic ? "تحديث التطبيق الإجباري" : "Force app update",
    subtitle: isArabic
      ? "تحكم في الحد الأدنى للإصدار وروابط المتاجر ورسائل التحديث."
      : "Control minimum versions, store links, and update messages.",
    forceSection: isArabic ? "التحديث الإجباري" : "Force update",
    softSection: isArabic ? "تحديث اختياري" : "Soft update",
    enableForce: isArabic ? "تفعيل التحديث الإجباري" : "Enable force update",
    enableSoft: isArabic ? "تفعيل التنبيه بالتحديث الاختياري" : "Enable soft update prompt",
    iosMin: isArabic ? "أقل إصدار iOS" : "iOS min version",
    androidMin: isArabic ? "أقل إصدار Android" : "Android min version",
    iosLatest: isArabic ? "أحدث إصدار iOS" : "iOS latest version",
    androidLatest: isArabic ? "أحدث إصدار Android" : "Android latest version",
    iosMinBuild: isArabic ? "أقل رقم بناء iOS (build)" : "iOS min build",
    androidMinBuild: isArabic ? "أقل رقم بناء Android (versionCode)" : "Android min build",
    iosStore: isArabic ? "رابط App Store" : "App Store URL",
    androidStore: isArabic ? "رابط Google Play" : "Google Play URL",
    forceMsgAr: isArabic ? "رسالة التحديث الإجباري (عربي)" : "Force message (Arabic)",
    forceMsgEn: isArabic ? "رسالة التحديث الإجباري (إنجليزي)" : "Force message (English)",
    softMsgAr: isArabic ? "رسالة التحديث الاختياري (عربي)" : "Soft message (Arabic)",
    softMsgEn: isArabic ? "رسالة التحديث الاختياري (إنجليزي)" : "Soft message (English)",
    save: isArabic ? "حفظ" : "Save",
    buildHint: isArabic
      ? "اترك 0 لتجاهل رقم البناء والاعتماد على رقم الإصدار فقط."
      : "Leave 0 to ignore build number and rely on version only.",
    versionHint: isArabic ? "مثال: 1.0.0" : "Example: 1.0.0",
  };

  const fetchSettings = async () => {
    try {
      const data = await forceUpdateSettingsService.get();
      setForm({ ...DEFAULT_FORM, ...data });
    } catch (error) {
      console.error("Failed to fetch force update settings:", error);
      setMessage({
        type: "error",
        text: isArabic ? "فشل تحميل الإعدادات" : "Failed to load settings",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSettings();
  }, []);

  const updateField = <K extends keyof ForceUpdateSettingsPayload>(
    key: K,
    value: ForceUpdateSettingsPayload[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    const versionFields: (keyof ForceUpdateSettingsPayload)[] = [
      "ios_min_version",
      "android_min_version",
      "ios_latest_version",
      "android_latest_version",
    ];

    for (const key of versionFields) {
      const value = String(form[key] ?? "").trim();
      if (!VERSION_RE.test(value)) {
        next[key] = isArabic ? "صيغة الإصدار غير صحيحة" : "Invalid version format";
      }
    }

    if (!form.force_message_ar.trim()) {
      next.force_message_ar = isArabic ? "الرسالة العربية مطلوبة" : "Arabic message is required";
    }
    if (!form.force_message_en.trim()) {
      next.force_message_en = isArabic ? "الرسالة الإنجليزية مطلوبة" : "English message is required";
    }

    for (const key of ["ios_store_url", "android_store_url"] as const) {
      const value = form[key].trim();
      if (value && !/^https?:\/\//i.test(value)) {
        next[key] = isArabic ? "الرابط يجب أن يبدأ بـ http أو https" : "URL must start with http or https";
      }
    }

    return next;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSaving(true);
    setMessage(null);
    try {
      const data = await forceUpdateSettingsService.update({
        ...form,
        ios_store_url: form.ios_store_url.trim(),
        android_store_url: form.android_store_url.trim(),
        ios_min_build: Number(form.ios_min_build) || 0,
        android_min_build: Number(form.android_min_build) || 0,
      });
      setForm({ ...DEFAULT_FORM, ...data });
      setMessage({
        type: "success",
        text: isArabic ? "تم حفظ الإعدادات بنجاح" : "Settings saved successfully",
      });
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        (isArabic ? "فشل حفظ الإعدادات" : "Failed to save settings");
      setMessage({ type: "error", text: apiMessage });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = INPUT_CLASS;

  return (
    <div className="mt-3" dir={isArabic ? "rtl" : "ltr"}>
      <Card extra="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">{labels.title}</h2>
          <p className="mt-1 text-sm text-gray-500">{labels.subtitle}</p>
        </div>

        {message ? (
          <div
            className={
              "mb-4 rounded-xl px-4 py-3 text-sm " +
              (message.type === "success"
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
                : "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-200")
            }
          >
            {message.text}
          </div>
        ) : null}

        {loading ? (
          <p className="py-10 text-center text-sm text-gray-500">
            {isArabic ? "جارٍ التحميل..." : "Loading..."}
          </p>
        ) : (
          <div className="space-y-8">
            <section className="space-y-4">
              <h3 className="text-base font-bold text-navy-700 dark:text-white">
                {labels.forceSection}
              </h3>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-navy-700 dark:text-white">
                  {labels.enableForce}
                </span>
                <ToggleSwitch
                  checked={form.force_update_enabled}
                  onChange={(next) => updateField("force_update_enabled", next)}
                  aria-label={labels.enableForce}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <SettingsField label={labels.iosMin} hint={labels.versionHint} error={errors.ios_min_version}>
                  <input
                    className={inputClass}
                    value={form.ios_min_version}
                    onChange={(e) => updateField("ios_min_version", e.target.value)}
                  />
                </SettingsField>
                <SettingsField
                  label={labels.androidMin}
                  hint={labels.versionHint}
                  error={errors.android_min_version}
                >
                  <input
                    className={inputClass}
                    value={form.android_min_version}
                    onChange={(e) => updateField("android_min_version", e.target.value)}
                  />
                </SettingsField>
                <SettingsField label={labels.iosMinBuild} hint={labels.buildHint} error={errors.ios_min_build}>
                  <input
                    type="number"
                    min={0}
                    className={inputClass}
                    value={form.ios_min_build}
                    onChange={(e) => updateField("ios_min_build", Number(e.target.value) || 0)}
                  />
                </SettingsField>
                <SettingsField
                  label={labels.androidMinBuild}
                  hint={labels.buildHint}
                  error={errors.android_min_build}
                >
                  <input
                    type="number"
                    min={0}
                    className={inputClass}
                    value={form.android_min_build}
                    onChange={(e) => updateField("android_min_build", Number(e.target.value) || 0)}
                  />
                </SettingsField>
                <SettingsField label={labels.iosStore} error={errors.ios_store_url}>
                  <input
                    className={inputClass}
                    value={form.ios_store_url}
                    onChange={(e) => updateField("ios_store_url", e.target.value)}
                    placeholder="https://apps.apple.com/..."
                  />
                </SettingsField>
                <SettingsField label={labels.androidStore} error={errors.android_store_url}>
                  <input
                    className={inputClass}
                    value={form.android_store_url}
                    onChange={(e) => updateField("android_store_url", e.target.value)}
                    placeholder="https://play.google.com/store/apps/details?id=..."
                  />
                </SettingsField>
                <SettingsField label={labels.forceMsgAr} error={errors.force_message_ar}>
                  <textarea
                    rows={3}
                    className={inputClass}
                    value={form.force_message_ar}
                    onChange={(e) => updateField("force_message_ar", e.target.value)}
                  />
                </SettingsField>
                <SettingsField label={labels.forceMsgEn} error={errors.force_message_en}>
                  <textarea
                    rows={3}
                    className={inputClass}
                    value={form.force_message_en}
                    onChange={(e) => updateField("force_message_en", e.target.value)}
                  />
                </SettingsField>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="text-base font-bold text-navy-700 dark:text-white">
                {labels.softSection}
              </h3>
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-navy-700 dark:text-white">
                  {labels.enableSoft}
                </span>
                <ToggleSwitch
                  checked={form.soft_update_enabled}
                  onChange={(next) => updateField("soft_update_enabled", next)}
                  aria-label={labels.enableSoft}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <SettingsField label={labels.iosLatest} error={errors.ios_latest_version}>
                  <input
                    className={inputClass}
                    value={form.ios_latest_version}
                    onChange={(e) => updateField("ios_latest_version", e.target.value)}
                  />
                </SettingsField>
                <SettingsField label={labels.androidLatest} error={errors.android_latest_version}>
                  <input
                    className={inputClass}
                    value={form.android_latest_version}
                    onChange={(e) => updateField("android_latest_version", e.target.value)}
                  />
                </SettingsField>
                <SettingsField label={labels.softMsgAr} error={errors.soft_message_ar}>
                  <textarea
                    rows={3}
                    className={inputClass}
                    value={form.soft_message_ar}
                    onChange={(e) => updateField("soft_message_ar", e.target.value)}
                  />
                </SettingsField>
                <SettingsField label={labels.softMsgEn} error={errors.soft_message_en}>
                  <textarea
                    rows={3}
                    className={inputClass}
                    value={form.soft_message_en}
                    onChange={(e) => updateField("soft_message_en", e.target.value)}
                  />
                </SettingsField>
              </div>
            </section>

            <div className={`flex ${isArabic ? "justify-start" : "justify-end"}`}>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave()}
                className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {saving ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : labels.save}
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ForceUpdateSettingsPage;
